import _ from 'lodash';
import { isIE } from 'react-device-detect';
import { DateTimeFormatter } from 'brock-date-time';
import i18next from '../shared/translations/i18n';
import {
    flattenArray,
    getGroupAxisFormatter,
} from './widgets/utils/dataFormatUtils';
import {
    defaultGridColumns,
    timeAndPrecisionConfigurationKeys,
    predicateValues,
    MAX_CHART_LABEL_WIDTH,
    MIN_CHART_LABEL_WIDTH,
    gridRowHeightPx,
    noDataIdentifier,
    gridRowHeightPxIE11,
    responsiveLayouts,
    minWindowWidths,
    inputTypes,
} from '../shared/constants/constants';
import {
    getPredicateOptions,
    getStringPredicateOptions,
} from '../shared/filtering/filtersModalUtils';
import { getDataByLanguage } from '../shared/translations/i18nUtils';

export function isLandscape(printViewMode) {
    return printViewMode.toLowerCase() === 'landscape';
}

// eslint-disable-next-line complexity
export function getFormattedValue(value, suffix, formatValue, formatType) {
    if (value === null || value === undefined) {
        return noDataIdentifier;
    }

    const formattedValue = formatValue ? formatValue(value, formatType) : value;
    const suffixValue = suffix ?? '';

    return typeof formattedValue === 'string'
        ? `${formattedValue}${suffixValue}`
        : `${Number(formattedValue).toLocaleString()}${suffixValue}`;
}

export function getGridTemplateStyles(numberOfGridRows) {
    const height = isIE ? gridRowHeightPxIE11 : gridRowHeightPx;

    return {
        msGridRows: `(${height}px)[${numberOfGridRows}]`,
        msGridColumns: `(1fr)[${defaultGridColumns}]`,
        gridTemplateRows: `repeat(${numberOfGridRows}, ${height}px)`,
        gridTemplateColumns: `repeat(${defaultGridColumns}, 1fr)`,
    };
}

export function getGridStyles({ row, column, rowSpan, columnSpan }) {
    return {
        msGridRow: `${row}`,
        msGridColumn: `${column}`,
        msGridRowSpan: `${rowSpan}`,
        msGridColumnSpan: `${columnSpan}`,
        gridArea: `${row} / ${column} / ${row + rowSpan} / ${
            column + columnSpan
        }`,
    };
}

function getSplitUpperCasedFriendlyString(str) {
    return (str.match(/[A-Z]*[a-z]+/g) || []).map(
        (part) => `${part[0].toUpperCase()}${part.slice(1)}`
    );
}

export function transformToFriendlyString(x) {
    const withReplacedUnderscores = `${x}`.replace(/_/g, ' ');
    const splitAndUpperCased = getSplitUpperCasedFriendlyString(
        withReplacedUnderscores
    );

    if (splitAndUpperCased.length > 1) {
        return splitAndUpperCased.join(' ');
    }

    if (splitAndUpperCased.length === 1) {
        return splitAndUpperCased[0];
    }

    return withReplacedUnderscores;
}

export function getXAxisLabel(label, xAxisProperties = {}) {
    const styles = {
        value: label,
        position: 'insideBottom',
        fill: '#575757',
    };
    if (xAxisProperties.labelWidth) {
        styles.width = xAxisProperties.labelWidth;
        styles.dy = xAxisProperties.labelPaddingTop;
    }
    return styles;
}

export function getXAxisDataKey(isVertical, firstGroupKey) {
    return isVertical ? null : firstGroupKey;
}

export function getXAxisTickFormatter(
    isVertical,
    groupAxisFormatter,
    dataFormatter,
    dataFormat,
    suffix
) {
    return isVertical
        ? (val) => getFormattedValue(val, suffix, dataFormatter, dataFormat)
        : groupAxisFormatter;
}

export function getYAxisDataKey(isVertical, firstGroupKey) {
    return isVertical ? firstGroupKey : null;
}

export function getYAxisTickFormatter(
    isVertical,
    groupAxisFormatter,
    dataFormatter,
    dataFormat,
    suffix
) {
    return isVertical
        ? groupAxisFormatter
        : (val) => getFormattedValue(val, suffix, dataFormatter, dataFormat);
}

function getYAxisLabelAngle(isLeft) {
    return isLeft ? -90 : 90;
}

function getYAxisLabelPosition(isLeft) {
    return isLeft ? 'insideLeft' : 'insideRight';
}

export function getYAxisLabel(label, isLeft = true) {
    return {
        value: label,
        angle: getYAxisLabelAngle(isLeft),
        position: getYAxisLabelPosition(isLeft),
        verticalAnchor: 'middle',
        fill: '#575757',
    };
}

function getTextWidth(text) {
    const canvas = document.getElementById('canvas');
    const ctx = canvas.getContext('2d');
    ctx.font = '12px Arial';

    return ctx.measureText(text).width;
}

// eslint-disable-next-line complexity
export function getChartMargin(
    dataPoints,
    groupKey,
    suffix,
    thresholds,
    tickFormat,
    tickFormatter,
    yAxisLabel,
    isVertical = false
) {
    let maxLabel = '';

    if (isVertical) {
        maxLabel = _.maxBy(
            dataPoints,
            (point) => `${point?.[groupKey]}`.length
        )?.[groupKey];
    } else {
        const data = dataPoints.map((point) => {
            const pointCopy = { ...point };
            delete pointCopy[groupKey];
            return pointCopy;
        });

        const dataLabels = flattenArray(
            data.map((x) =>
                _.values(x).map((val) =>
                    getFormattedValue(val, suffix, tickFormatter, tickFormat)
                )
            )
        );

        const thresholdLabels = thresholds.map((x) =>
            getFormattedValue(x.value, suffix, tickFormatter, tickFormat)
        );

        maxLabel = _.maxBy(dataLabels.concat(thresholdLabels), (x) => x.length);
    }

    const yAxisLabelWidth = yAxisLabel
        ? Math.max(MIN_CHART_LABEL_WIDTH, getTextWidth(maxLabel || 0) + 40)
        : getTextWidth(maxLabel || 0) + 20;

    return { left: Math.min(MAX_CHART_LABEL_WIDTH, yAxisLabelWidth) };
}

export function getSankeyChartMargin(
    dataPoints,
    dataFormatter,
    dataFormat,
    suffix
) {
    const maxNodeLabel = _.maxBy(
        dataPoints.nodes,
        (point) => point.name.length
    ).name;
    const maxNodeLabelWidth = getTextWidth(maxNodeLabel);

    const maxValueLabel = flattenArray(
        dataPoints.links.map((x) =>
            _.values(x).map((val) =>
                getFormattedValue(val, suffix, dataFormatter, dataFormat)
            )
        )
    );
    const maxValueLabelWidth = getTextWidth(
        _.maxBy(maxValueLabel, (x) => x.length)
    );

    return {
        right: Math.max(maxNodeLabelWidth, maxValueLabelWidth) + 25,
        top: 15,
        bottom: 15,
    };
}

export function getBiaxialChartMargin(
    dataPoints,
    groupKey,
    leftDataLabels,
    rightDataLabels,
    leftTickFormatter,
    rightTickFormatter
) {
    const data = dataPoints.map((point) => {
        const pointCopy = { ...point };
        delete pointCopy[groupKey];
        return pointCopy;
    });

    const leftFormattedLabels = flattenArray(
        data.map((dataPoint) =>
            _(dataPoint)
                .pickBy((value, key) => leftDataLabels.includes(key))
                .values()
                .map((val) => leftTickFormatter(val))
                .value()
        )
    );

    const rightFormattedLabels = flattenArray(
        data.map((dataPoint) =>
            _(dataPoint)
                .pickBy((value, key) => rightDataLabels.includes(key))
                .values()
                .map((val) => rightTickFormatter(val))
                .value()
        )
    );

    const maxLeftLabel = _.maxBy(leftFormattedLabels, (x) => x.length);
    const maxRightLabel = _.maxBy(rightFormattedLabels, (x) => x.length);

    return {
        right: Math.min(
            MAX_CHART_LABEL_WIDTH,
            Math.max(MIN_CHART_LABEL_WIDTH, getTextWidth(maxRightLabel) + 40)
        ),
        left: Math.min(
            MAX_CHART_LABEL_WIDTH,
            Math.max(MIN_CHART_LABEL_WIDTH, getTextWidth(maxLeftLabel) + 40)
        ),
    };
}

export function shouldShowDashboardFilter(
    showTimeAndPrecision,
    availableFilters,
    requiredFilterKeys,
    selectOptions
) {
    if (showTimeAndPrecision) {
        return true;
    }

    const filtersAreNotHandled = Object.keys(availableFilters).some(
        (filterKey) => {
            if (!requiredFilterKeys.includes(filterKey)) {
                return true;
            }
            const selectOption = selectOptions.find(
                (x) => x.fieldName === availableFilters[filterKey].apiKey
            );
            return !selectOption || selectOption?.options.length !== 1;
        }
    );

    return !_.isEmpty(availableFilters) && filtersAreNotHandled;
}

export function isTimeAndPrecisionKey(filterKey) {
    return _.values(timeAndPrecisionConfigurationKeys).includes(filterKey);
}

export function isAvailableFilterKey(filterKey, availableFilters = {}) {
    return Object.keys(availableFilters).includes(filterKey);
}

export function isWhereClauseKey(filterKey, whereClauseOptions = []) {
    return whereClauseOptions.includes(filterKey);
}

export function buildFilterOptionObject(value) {
    return {
        textField: value?.textField ?? value,
        valueField: value?.valueField ?? value,
    };
}

function findChildOptionByValue(options, value) {
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < options.length; i++) {
        const option = options[i];
        // eslint-disable-next-line eqeqeq
        if (option.valueField == (value?.valueField ?? value)) {
            return option;
        }

        if (option.children?.length) {
            const childOption = findChildOptionByValue(option.children, value);
            if (childOption) {
                return childOption;
            }
        }
    }

    return undefined;
}

export function findOptionByValue(options, value) {
    if (!options?.length) {
        return buildFilterOptionObject(value);
    }

    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < options.length; i++) {
        const option = options[i];
        // eslint-disable-next-line eqeqeq
        if (option.valueField == (value?.valueField ?? value)) {
            return option;
        }

        if (option.children?.length) {
            const childOption = findChildOptionByValue(option.children, value);
            if (childOption) {
                return childOption;
            }
        }
    }

    return buildFilterOptionObject(value);
}

function getSelectOptionsByFilterKey(
    filterKey,
    selectOptions,
    availableFilters
) {
    const availableFilter = availableFilters[filterKey];
    const selectOption = selectOptions.find(
        (x) => x.fieldName === availableFilter?.apiKey
    );

    return selectOption?.options ?? [];
}

/* eslint-disable complexity */
export function getDashboardStateFromQueryParams(
    queryParams,
    availableFilters,
    whereClauseOptions,
    selectOptions,
    predicateLabels
) {
    const urlTimeAndPrecision = {};
    const urlFilterValues = {};
    const urlWhereClauses = [];
    const urlRecordLimit = {};

    if (!_.isEmpty(queryParams)) {
        _.forEach(queryParams, (urlValue, filterKey) => {
            if (isTimeAndPrecisionKey(filterKey)) {
                urlTimeAndPrecision[filterKey] = urlValue;
                return;
            }

            if (isAvailableFilterKey(filterKey, availableFilters)) {
                const options = getSelectOptionsByFilterKey(
                    filterKey,
                    selectOptions,
                    availableFilters
                );

                const filterValuesFromOptions = Array.isArray(urlValue)
                    ? urlValue.map((value) => findOptionByValue(options, value))
                    : [findOptionByValue(options, urlValue)];

                urlFilterValues[filterKey] = {
                    predicate: {
                        textField: predicateLabels.isIn,
                        valueField: predicateValues.isIn,
                    },
                    values: filterValuesFromOptions.filter((x) => x),
                };
                return;
            }

            if (isWhereClauseKey(filterKey, whereClauseOptions)) {
                urlWhereClauses.push({
                    propertyName: filterKey,
                    predicate: {
                        textField: predicateLabels.is,
                        valueField: predicateValues.is,
                    },
                    values: [urlValue],
                });
                return;
            }

            if (filterKey === 'recordLimit') {
                urlRecordLimit.recordLimit = urlValue;
            }

            if (filterKey === 'isDescending') {
                urlRecordLimit.isDescending = urlValue === 'true';
            }
        });
    }

    return {
        urlTimeAndPrecision,
        urlFilterValues,
        urlWhereClauses,
        urlRecordLimit,
    };
}

function setStringFilterBasedOnLanguage(predicate, values, predicateLabels) {
    const predicateOptions = getStringPredicateOptions(predicateLabels);

    const predicateByLanguage = predicateOptions.find(
        (x) => x.valueField === predicate.valueField
    );

    return {
        predicate: predicateByLanguage,
        values,
    };
}

function setSelectFilterBasedOnLanguage(
    predicate,
    values,
    filterKey,
    selectOptions,
    availableFilters,
    predicateLabels
) {
    const predicateOptions = getPredicateOptions(predicateLabels);

    const predicateByLanguage =
        predicateOptions.find((x) => x.valueField === predicate.valueField) ??
        predicate;

    const options = getSelectOptionsByFilterKey(
        filterKey,
        selectOptions,
        availableFilters
    );

    const filterValuesFromOptions = Array.isArray(values)
        ? values.map((value) => findOptionByValue(options, value))
        : [findOptionByValue(options, values)];

    return {
        predicate: predicateByLanguage,
        values: filterValuesFromOptions.filter((x) => x),
    };
}

function setFilterBasedOnLanguage(
    { predicate, values },
    filterKey,
    selectOptions,
    availableFilters,
    predicateLabels
) {
    const availableFilter = availableFilters[filterKey];

    if (availableFilter?.type === inputTypes.string) {
        return setStringFilterBasedOnLanguage(
            predicate,
            values,
            predicateLabels
        );
    }

    return setSelectFilterBasedOnLanguage(
        predicate,
        values,
        filterKey,
        selectOptions,
        availableFilters,
        predicateLabels
    );
}

export function setFiltersBasedOnLanguage(
    configuration,
    selectOptions,
    availableFilters,
    predicateLabels
) {
    // eslint-disable-next-line no-param-reassign
    configuration.filterValues = _.mapValues(
        configuration.filterValues,
        (filterValue, filterKey) =>
            setFilterBasedOnLanguage(
                filterValue,
                filterKey,
                selectOptions,
                availableFilters,
                predicateLabels
            )
    );
}

export function getNumberDecimalFormatter(numberOfDecimals) {
    return (value) => {
        if (!value && value !== 0) {
            return noDataIdentifier;
        }

        return typeof value === 'string'
            ? value
            : Number(Number(value).toFixed(numberOfDecimals));
    };
}
/* eslint-enable complexity */

export const numberDecimalFormatters = {
    noDecimal: getNumberDecimalFormatter(0),
    oneDecimal: getNumberDecimalFormatter(1),
    twoDecimal: getNumberDecimalFormatter(2),
    threeDecimal: getNumberDecimalFormatter(3),
};

export function getClientTimeStamp() {
    return DateTimeFormatter.dateTime(Date.now());
}

export function getKeyFromSavedDashboardName(name) {
    return name.toLowerCase().split(' ').join('');
}

export function getActiveWidgets(panels, widgets, activePanelTabs) {
    const activeWidgets = panels.map((panel) => {
        if (panel.tabs) {
            const activeTab = activePanelTabs[panel.id] || panel.tabs[0].id;
            return widgets.filter(
                (widget) =>
                    // eslint-disable-next-line eqeqeq
                    widget.tabId == activeTab && widget.panelId === panel.id
            );
        }
        return widgets.filter((widget) => widget.panelId === panel.id);
    });

    return flattenArray(activeWidgets);
}

export function getDashboardContextValue(
    props,
    selectOptions,
    thresholdValues,
    widgetRenderers,
    systemManifest
) {
    return {
        assets: props.assets,
        customizations: props.customizations,
        favoriteUtilities: props.favoriteUtilities,
        selectOptions,
        thresholdValues,
        uriConstants: props.uriConstants,
        widgetRenderers,
        systemManifest,
    };
}

export function getCustomLabelsFromI18n() {
    const labels = getDataByLanguage(i18next.language);
    return labels.customLabels;
}

export function getChartRenderProps(
    widget,
    metricRequests,
    metricData,
    thresholdsWithValues,
    legendFormatter,
    dashboardLabels,
    openLinksInNewTab = true,
    setDownloadData = () => {},
    onContextMenuOpen = () => {},
    onSelectorClick = () => {},
    onSelectorRemoveClick = () => {}
) {
    return {
        actions: widget.actions,
        calculatedMetricConfiguration: widget.calculatedMetricConfiguration,
        configuration: widget.configuration,
        customLabels: getCustomLabelsFromI18n(),
        dashboardLabels,
        friendlyLegendLabels: widget.friendlyLegendLabels,
        groupAxisFormatter: getGroupAxisFormatter(
            widget.groupAxisFormatType,
            widget.groupAxisFormatProps,
            legendFormatter
        ),
        isSelectable: !!widget.publisherFilters?.length,
        legendFormatter,
        metricRequests,
        metrics: metricData,
        numberDecimalFormatter: widget.numberDecimalFormatter,
        onContextMenuOpen,
        onSelectorClick,
        onSelectorRemoveClick,
        openLinksInNewTab,
        selectors: widget.selectors,
        setDownloadData,
        thresholds: thresholdsWithValues,
        xAxisLabel: widget.xAxisLabel,
        yAxisLabelLeft: widget.yAxisLabelLeft,
        yAxisLabelRight: widget.yAxisLabelRight,
    };
}

export function hasFilterWarning(
    dashboardFilterValues,
    inapplicableFilterKeys
) {
    if (_.isEmpty(dashboardFilterValues) || _.isEmpty(inapplicableFilterKeys)) {
        return null;
    }
    return _.keys(dashboardFilterValues).some(
        (filterKey) =>
            inapplicableFilterKeys.includes(filterKey) &&
            !_.isEmpty(dashboardFilterValues[filterKey].values)
    );
}

export function joinRecordLimit(recordLimit, isDescending) {
    return {
        recordLimit,
        isDescending,
    };
}

function getSelectOptionsObjectByValue(selectOptions, apiKey, value) {
    if (
        !selectOptions
            .find((options) => options.fieldName === apiKey)
            .options.find((option) => option.valueField === value)
    ) {
        return {
            textField: value,
            valueField: value,
        };
    }
    return selectOptions
        .find((options) => options.fieldName === apiKey)
        .options.find((option) => option.valueField === value);
}

function getStringFilterLocalStorageItemByKey(key, defaultValue) {
    try {
        const value = localStorage.getItem(key);
        if (!value) {
            return defaultValue;
        }
        return {
            predicate: {
                textField: i18next.t('predicateLabels:contains'),
                valueField: predicateValues.contains,
            },
            values: value ? [value] : [],
        };
    } catch {
        return defaultValue;
    }
}

function getSelectFilterLocalStorageItemByKey(
    selectOptions,
    apiKey,
    key,
    defaultValue
) {
    try {
        const values = JSON.parse(localStorage.getItem(key));
        if (!values) {
            return defaultValue;
        }
        return {
            predicate: {
                textField: i18next.t('predicateLabels:isIn'),
                valueField: predicateValues.isIn,
            },
            values: values?.map((value) =>
                getSelectOptionsObjectByValue(selectOptions, apiKey, value)
            ),
        };
    } catch {
        return defaultValue;
    }
}

export function getLastUsedFilterValues(
    filterConfigurations,
    selectOptions,
    filterValues
) {
    if (filterConfigurations) {
        const lastUsedFilterValues = {};
        const filterKeys = Object.keys(filterConfigurations).filter(
            (x) => filterConfigurations[x].useRememberLastUsedValue
        );

        // eslint-disable-next-line complexity
        filterKeys.forEach((key) => {
            if (filterConfigurations[key].type === inputTypes.string) {
                lastUsedFilterValues[key] =
                    getStringFilterLocalStorageItemByKey(
                        filterConfigurations[key].lastUsedValueKey ?? key,
                        filterValues[key]
                    );
            } else {
                lastUsedFilterValues[key] =
                    getSelectFilterLocalStorageItemByKey(
                        selectOptions,
                        filterConfigurations[key].apiKey,
                        filterConfigurations[key].lastUsedValueKey ?? key,
                        filterValues[key]
                    );
            }
        });

        return lastUsedFilterValues;
    }
    return {};
}

export function getRequiredFiltersWithOneOption(
    filterConfigurations,
    requiredFilterKeys,
    selectOptions
) {
    if (!requiredFilterKeys?.length || !filterConfigurations) {
        return {};
    }

    return requiredFilterKeys.reduce((result, filterKey) => {
        if (!filterConfigurations?.[filterKey]) {
            return result;
        }

        const selectOption = selectOptions.find(
            (x) => x.fieldName === filterConfigurations[filterKey].apiKey
        );

        if (selectOption?.options?.length !== 1) {
            return result;
        }

        return {
            ...result,
            [filterKey]: {
                predicate: {
                    valueField: predicateValues.isIn,
                    textField: i18next.t('predicateLabels:isIn'),
                },
                values: [
                    {
                        valueField: selectOption.options[0].valueField,
                        textField: selectOption.options[0].textField,
                    },
                ],
            },
        };
    }, {});
}

export function formatViewConfiguration(viewConfiguration, selectOptions) {
    return {
        dashboardTimeAndPrecision: viewConfiguration.timeAndPrecision,
        dashboardFilterValues: {
            ...getRequiredFiltersWithOneOption(
                viewConfiguration.filterConfigurations,
                viewConfiguration.requiredFilterKeys,
                selectOptions
            ),
            ...viewConfiguration.filterValues,
            ...getLastUsedFilterValues(
                viewConfiguration.filterConfigurations,
                selectOptions,
                viewConfiguration.filterValues
            ),
        },
        dashboardWhereClauses: viewConfiguration.whereClauses,
        dashboardRecordLimit: {
            recordLimit: viewConfiguration.recordLimit,
            isDescending: !!viewConfiguration.isDescending,
        },
        metricData: viewConfiguration.savedMetricData ?? [],
        key: viewConfiguration.key,
        title: viewConfiguration.title,
        panels: viewConfiguration.panels,
        widgets: viewConfiguration.widgets,
        viewKey: viewConfiguration.viewKey,
    };
}

// eslint-disable-next-line complexity
export function getResponsiveLayoutMode(
    selectedResponsiveLayout,
    dashboardWidth
) {
    if (selectedResponsiveLayout !== responsiveLayouts.auto) {
        return selectedResponsiveLayout;
    }

    if (dashboardWidth < minWindowWidths.tablet) {
        return responsiveLayouts.mobile;
    }

    if (
        dashboardWidth >= minWindowWidths.tablet &&
        dashboardWidth < minWindowWidths.desktop
    ) {
        return responsiveLayouts.tablet;
    }

    return responsiveLayouts.desktop;
}

// eslint-disable-next-line complexity
export function getNumberOfColumnsFromPanelConfig(panel, layoutMode) {
    if (layoutMode === responsiveLayouts.mobile) {
        return (
            panel.layoutSettings?.mobile?.numberOfColumns ??
            panel.numberOfColumns ??
            1
        );
    }

    if (layoutMode === responsiveLayouts.tablet) {
        return (
            panel.layoutSettings?.tablet?.numberOfColumns ??
            panel.numberOfColumns ??
            1
        );
    }

    return panel.numberOfColumns ?? 1;
}
